import PropTypes from 'prop-types';
// import { useTranslation } from 'react-i18next';

import classes from './AditionalMaterialLesson.module.css';

/**
 * AditionalMaterialLesson Component
 *
 * Displays a list of additional lesson materials (files/documents) for a lesson.
 * The component adapts its UI and behavior based on the user's access rights:
 * - With access: Materials are clickable and open in new tabs
 * - Without access: Materials are displayed as read-only
 *
 * Features:
 * - Dynamic rendering of lesson materials based on access permissions
 * - Clickable file links for authorized users
 * - Responsive design for both access and no-access states
 * - Multi-language support via i18n
 * - Accessibility with alt text for icons
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Array} props.aditionalMaterial - Array of material objects containing file information
 * @param {boolean} props.accessLesson - Whether the user has access to the lesson materials
 * @returns {JSX.Element} The rendered additional materials section
 */
const AditionalMaterialLesson = props => {
  // const { t } = useTranslation(['lesson']);

  /**
   * Creates a function to open a file in a new browser tab
   * @param {string} url - The URL of the file (currently unused)
   * @param {string} path - The file path to open
   * @returns {Function} Event handler function
   */
  const openFile = (url, path) => () => {
    window.open(`${path}`, '_blank');
  };

  return (
    <div
      className={
        props.accessLesson
          ? classes.lessonMaterialAccessSection
          : classes.lessonMaterialSection
      }
    >
      {/* <h2 className={classes.includesTitle}>
        {t('aditional_files')}{' '}
      </h2> */}
      {/* Render materials list only if materials exist */}
      {props.aditionalMaterial && (
        <aside className={classes.lessonContentItemsContainer}>
          {props.aditionalMaterial.map(material => (
            <p
              key={material.idArchivoVideo}
              className={`${
                props.accessLesson ? classes.materialClick : ''
              }`}
              onClick={
                /* Only allow clicking if user has lesson access */
                props.accessLesson &&
                openFile(material.isURL, material.path)
              }
            >
              {/* Material file icon */}
              <figure className={classes.lessonIcon}>
                <img
                  className={classes.img}
                  src="https://d1g6c01s9ssd7h.cloudfront.net/img/Documento_icon.png"
                  alt="Document icon" // Accessibility: icon alt text
                />
              </figure>
              {/* Material name/title */}
              <span className={classes.lessonContentItemText}>
                {material.name}{' '}
                {/* File/document name passed as prop to this span */}
              </span>
            </p>
          ))}
        </aside>
      )}
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
AditionalMaterialLesson.propTypes = {
  /**
   * Array of additional material objects for the lesson
   * Each material object should contain:
   * - idArchivoVideo: unique identifier for the material
   * - name: display name of the material
   * - path: file path or URL to the material
   * - isURL: boolean indicating if the path is a URL
   */
  aditionalMaterial: PropTypes.arrayOf(
    PropTypes.shape({
      idArchivoVideo: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]),
      name: PropTypes.string, // File/document name used in child span
      path: PropTypes.string, // File/document path used for opening
      isURL: PropTypes.bool // Indicates if path is a URL
    })
  ),
  /**
   * Boolean indicating whether the user has access to the lesson materials
   * When true, materials become clickable and open in new tabs
   * Passed as prop to control click behavior and styling
   */
  accessLesson: PropTypes.bool
};

AditionalMaterialLesson.defaultProps = {
  aditionalMaterial: [],
  accessLesson: false
};

export default AditionalMaterialLesson;
